import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';

import { DSTokenMap, MQ, ZIndex } from '@rover/kibble/styles';

import ServerModal from './ServerModal';

export type Props = {
  className?: string;
  disablePortal: boolean;
  closeTimeoutMS: number;
  onRequestClose: () => void;
  data?: Record<string, any>;
  isOpen: boolean;
  children: any;
  aria: Record<string, any>;
  style?: {
    content?: Record<string, string | number>;
    overlay?: Record<string, string | number>;
  };
};

const ModalStylingAdapter = ({
  className,
  disablePortal,
  closeTimeoutMS,
  onRequestClose,
  data,
  isOpen,
  style,
  ...other
}: Props): JSX.Element => {
  const contentClassName = `${className} RoverModalContent`;
  const overlayClassName = `${className} RoverModalOverlay`;

  const [isPortalDisabled, setIsPortalDisabled] = useState(disablePortal);

  // once on client side, swap to ReactModal
  useEffect(() => {
    if (isPortalDisabled) {
      setIsPortalDisabled(false);
    }
  }, [isPortalDisabled]);

  if (isPortalDisabled) {
    return (
      <ServerModal
        onRequestClose={onRequestClose}
        closeTimeoutMS={closeTimeoutMS}
        data={data}
        isOpen={isOpen}
        className={className}
        contentClassName={contentClassName}
        overlayClassName={overlayClassName}
        style={style}
        {...other}
      />
    );
  }

  return (
    <ReactModal
      isOpen={isOpen}
      data={data}
      onRequestClose={onRequestClose}
      closeTimeoutMS={closeTimeoutMS}
      portalClassName={className}
      className={contentClassName}
      overlayClassName={overlayClassName}
      {...other}
    />
  );
};

const StyledModal = styled(ModalStylingAdapter)`
  // This component needs to have a width & height so that the React modal's accessibility focus components work.
  width: 100vw;
  height: 100%;
  max-width: 100%;
  position: absolute;
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  z-index: ${ZIndex.MODAL.toString()};

  &.RoverModalOverlay {
    max-height: 100%;
    max-width: 100vw;
    inset: 0px;
    z-index: ${ZIndex.MODAL.toString()};
    transition: background-color 0.15s ease-in;
    background-color: ${(props) =>
      props.disablePortal ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0)'} !important;

    ${MQ.SM_UP.toString()} {
      overflow-y: auto;
      padding-top: 110px;
    }

    &.ReactModal__Overlay--after-open {
      // This component needs to have a width & height so that the React modal's accessibility focus components work.
      width: 100vw;
      height: 100%;
      position: fixed;
      top: 0px;
      left: 0px;
      background-color: rgba(0, 0, 0, 0.8) !important;
    }

    &.ReactModal__Overlay--before-close {
      background-color: rgba(0, 0, 0, 0) !important;
    }
  }

  &.RoverModalContent {
    background: ${DSTokenMap.BACKGROUND_COLOR_PRIMARY.toString()};
    height: 100%;
    inset: auto;
    margin: 0 auto;
    width: 100vw;
    padding: 0px !important;
    position: static !important;
    transition: opacity 0.15s linear, transform 0.15s ease-out;
    display: flex;
    flex-direction: column;

    // if modal is open on load, don't allow after-open transition
    ${(props) =>
      props.disablePortal
        ? ''
        : css`
            opacity: 0;
          `}

    ${MQ.SM_UP.toString()} {
      height: auto;
      margin-bottom: 110px;
      max-width: 600px;
      min-height: 100px;
      padding: ${DSTokenMap.SPACE_8X} !important;
      width: auto;

      // if modal is open on load, don't allow after-open transition
      ${(props) =>
        props.disablePortal
          ? ''
          : css`
              transform: translateY(-25%);
            `}

      &.ReactModal__Content--before-close {
        transform: translateY(-10%) !important;
      }
    }

    &.ReactModal__Content--after-open {
      opacity: 1;
      overflow: auto;
      transform: translateY(0);
      // transform causes the element to become a containing block. Even though this element has position: static, positioned descendants will be relative to this element's box model.
    }
  }
`;

export default StyledModal;
